
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { invokeEdgeFunction } from "@/utils/edgeFunctionUtils";

const Authorize = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [ssoSession, setSsoSession] = useState<string | null>(null);

  useEffect(() => {
    // Check for SSO session from Discourse
    const ssoSessionId = searchParams.get('sso_session');
    if (ssoSessionId) {
      setSsoSession(ssoSessionId);
      console.log('🔄 [Authorize] SSO session detected:', ssoSessionId);
    }

    console.log('🔄 [Authorize] Checking initial session...');
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        
        if (session) {
          console.log('✅ [Authorize] Session found:', session);
          
          // If this is an SSO flow, complete it
          if (ssoSessionId) {
            console.log('🔄 [Authorize] Completing SSO flow...');
            console.log('🔑 [Authorize] Auth token:', session.access_token);
            
            try {
              // Construct the proper URL format with parameters in the path
              const projectRef = "qulnbyjrczvoxemtrili";
              const baseUrl = `https://${projectRef}.functions.supabase.co`;
              const ssoUrl = new URL(`${baseUrl}/functions/v1/discourse-sso`);
              
              // Add parameters to URL
              ssoUrl.searchParams.append('sso_session', ssoSessionId);
              ssoUrl.searchParams.append('auth_token', session.access_token);
              
              console.log('🔀 [Authorize] Redirecting to:', ssoUrl.toString());
              
              // Use window.location to perform the redirect
              window.location.href = ssoUrl.toString();
              return;
            } catch (ssoError) {
              console.error('❌ [Authorize] Error in SSO flow:', ssoError);
              toast({
                variant: "destructive",
                title: "SSO Error",
                description: "There was a problem with the SSO process. Please try again.",
              });
            }
          } else {
            // Regular login, redirect to companies
            navigate('/companies');
          }
        } else {
          console.log('ℹ️ [Authorize] No session found, showing login form');
        }
      } catch (error) {
        console.error('❌ [Authorize] Error checking session:', error);
        toast({
          variant: "destructive",
          title: "Authentication Error",
          description: "There was a problem checking your login status.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔔 [Authorize] Auth state changed:', event, session?.user?.email);
      
      if (session) {
        console.log('✅ [Authorize] User authenticated');
        
        // If this is an SSO flow, handle it
        if (ssoSessionId) {
          console.log('🔄 [Authorize] Completing SSO flow after login...');
          console.log('🔑 [Authorize] Auth token:', session.access_token);
          
          // Construct the proper URL format with parameters
          const projectRef = "qulnbyjrczvoxemtrili";
          const baseUrl = `https://${projectRef}.functions.supabase.co`;
          const ssoUrl = new URL(`${baseUrl}/functions/v1/discourse-sso`);
          
          // Add parameters to URL
          ssoUrl.searchParams.append('sso_session', ssoSessionId);
          ssoUrl.searchParams.append('auth_token', session.access_token);
          
          console.log('🔀 [Authorize] Redirecting to:', ssoUrl.toString());
          
          // Use window.location to perform the redirect
          window.location.href = ssoUrl.toString();
        } else {
          // Regular login flow
          navigate('/companies');
        }
      }
    });

    return () => {
      console.log('👋 [Authorize] Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, [navigate, searchParams, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-sm w-full max-w-md">
        {ssoSession ? (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Sign in to continue to Discourse</h2>
            <p className="text-gray-600 mb-6 text-center">
              Please sign in to your account to continue to Discourse.
            </p>
          </>
        ) : (
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Sign in to manage projects</h2>
        )}
        <Auth
          supabaseClient={supabase}
          appearance={{ theme: ThemeSupa }}
          providers={['google']}
          theme="light"
        />
      </div>
    </div>
  );
};

export default Authorize;
